import { FacilityRoomApi } from '@app/api';
import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { FormAddress } from '@app/components/FormAddress';
import InlineEdit from '@app/components/InLineEdit';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { isNotFound } from '@app/utils/ApiError';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { HARDWARE_LEVEL } from '@app/model/Enums';
import { Facility, FacilityRoom } from '@app/model/Facility';
import { useFacilityStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, AlertVariant, Button, Card, Checkbox, DescriptionList, DescriptionListDescription, DescriptionListGroup, DescriptionListTerm, FormSelect, FormSelectOption, Icon, PageSection, Pagination, PaginationVariant, SplitItem, TextArea, TextInput, Title, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Tooltip } from '@patternfly/react-core';
import { CheckIcon, PencilAltIcon, TimesIcon, } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const FacilityDetail: React.FunctionComponent = () => {
  type Action = { type: 'load' | 'update', payload: Facility };

  const { id } = useParams();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  
  // 1. Grab both global data management methods from the store
  const loadFacility = useFacilityStore(state => state.loadById);
  const saveFacility = useFacilityStore(state => state.save);
  const removeFacility = useFacilityStore(state => state.delete);

  const [page, setPage] = React.useState<number>(1);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  const [perPage, setPerPage] = React.useState<number>(5);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  /** No facility answers to that id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState<boolean>(false);

  const facilityUpdater = (state: Facility, action: Action) => {
    switch (action.type) {
      case 'load': return action.payload;
      case 'update':
        setIsDirty(true);
        return action.payload;
      default: return state;
    }
  };

  const [facility, setFacility] = React.useReducer(facilityUpdater, {} as Facility);

  useBreadcrumbLabel(id === 'new' ? 'New' : facility.name);

  const columns = ['Name', 'Capacity', 'Hardware Level', 'Dual Monitors', 'Comments', 'Actions'];
  const [isEditEnabled, setIsEditEnabled] = React.useState<boolean>(false);
  
  const [room, setRoom] = React.useState<FacilityRoom>({} as FacilityRoom);
  const [isEditRoomEnabled, setIsEditRoomEnabled] = React.useState<boolean>(false);
  const [isAddingRoom, setIsAddingRoom] = React.useState<boolean>(false);

  const updateFacility = (updatedFacility: Facility) => setFacility({ type: 'update', payload: updatedFacility });

  // --- Derived Pagination ---
  const rooms = facility.rooms || [];
  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return rooms.slice(startIdx, startIdx + perPage);
  }, [rooms, page, perPage]);

  // --- Effects ---
  React.useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (id === 'new') {
        setFacility({ type: 'load', payload: {} as Facility });
        setIsEditEnabled(true);
        return;
      }

      setNotFound(false);
      beginLoading();
      try {
        // 2. Uses the smart store loader (checks cache automatically)
        const data = await loadFacility(id!);
        if (isMounted) setFacility({ type: 'load', payload: data });
      } catch (error: any) {
        // An id matching no facility is the answer rather than a failure: the page renders it.
        if (!isMounted) return;
        if (isNotFound(error)) setNotFound(true);
        else addNotification('Error loading facility data: ' + error.message, AlertVariant.danger);
      } finally {
        endLoading();
      }
    };
    
    loadData();
    return () => { isMounted = false; };
  }, [id, loadFacility]); 

  // --- Pagination Handlers ---
  const handleSetPage = (_evt: any, newPage: number) => setPage(newPage);
  const handlePerPageSelect = (_evt: any, newPerPage: number, newPage: number) => {
    setPage(newPage);
    setPerPage(newPerPage);
  };

  // --- Utility: Re-sync component and store after a room edit ---
  const refreshFacilityState = async (facId: string | number) => {
    // 3. Passing true bypasses the cache. 
    // The loadFacility method inherently updates the store cache upon resolving,
    // so we no longer need to call upsertItem manually!
    const freshFacility = await loadFacility(facId, true); 
    setFacility({ type: 'load', payload: freshFacility });
  };

  // --- Handlers ---
  const onSaveClick = async () => {
    beginLoading();
    try {
      // 4. Store handles the save AND cache update!
      const savedData = await saveFacility(facility, id === 'new');
      
      setFacility({ type: 'load', payload: savedData });
      setIsDirty(false);
      addNotification("Facility saved with success!", AlertVariant.success);

      if (id === 'new') navigate(`/facilities/${savedData.id}`, { replace: true });
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onCancelClick = async () => {
    setIsDirty(false);
    if (id === 'new') {
      setFacility({ type: 'load', payload: {} as Facility });
    } else {
      // 5. Instantly restores from the cache
      const data = await loadFacility(id!);
      setFacility({ type: 'load', payload: data });
    }
  };

  const onNewRoomClick = () => {
    setIsEditRoomEnabled(true);
    setIsAddingRoom(true);
    setRoom({} as FacilityRoom);
  };

  const onEditRoomClick = (event: React.MouseEvent | KeyboardEvent, roomToEdit: FacilityRoom) => {
    setIsEditRoomEnabled(true);
    setIsAddingRoom(false);
    setRoom(roomToEdit);
  };

  const onRoomSave = async () => {
    beginLoading();
    try {
      if (room.id === undefined) {
        await FacilityRoomApi.create(facility.id, room);
      } else {
        await FacilityRoomApi.update(facility.id, room.id, room);
      }
      
      await refreshFacilityState(facility.id);
      
      addNotification("Room saved with success!", AlertVariant.success);
      setIsEditRoomEnabled(false);
      setIsAddingRoom(false);
      setRoom({} as FacilityRoom);
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onRefresh = async () => {
    if (id === 'new') return;
    await refreshFacilityState(id!);
    setIsDirty(false);
  };

  const onRemove = async () => {
    beginLoading();
    try {
      await removeFacility(facility.id);
      addNotification("Facility removed", AlertVariant.success, 'Facility removed with success');
      // Same state the layout's Back button carries, so the list comes back filtered as it was
      navigate('/facilities', { state: { restoreFilters: true } });
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger, "Error removing facility");
    } finally {
      endLoading();
    }
  };

  const onRemoveRoom = async (roomId: string | number) => {
    beginLoading();
    try {
      await FacilityRoomApi.remove(facility.id, roomId);
      await refreshFacilityState(facility.id);
      addNotification("Room removed with success!", AlertVariant.success);
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onRoomCancel = () => {
    setIsEditRoomEnabled(false);
    setIsAddingRoom(false);
    setRoom({} as FacilityRoom);
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={facility.rooms?.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      perPageOptions={[{ title: '5', value: 5 }, { title: '10', value: 10 }, { title: '20', value: 20 }]}
      variant={variant as PaginationVariant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const tableToolbar = (
    <Toolbar id="compact-toolbar">
      <ToolbarContent>
        <ToolbarGroup>
          <ToolbarItem>
            <Button variant="primary" onClick={onNewRoomClick}>New room</Button>
          </ToolbarItem>
        </ToolbarGroup>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const roomEditForm = (
    <Tr key='new'>
      <Td dataLabel={columns[0]}>
        <TextInput value={room.name || ''} onChange={(ev, v) => setRoom({ ...room, name: v })}
          type="text" isRequired id="name" name="name" />
      </Td>
      <Td dataLabel={columns[1]}>
        <TextInput value={room.capacity || ''} onChange={(ev, v) => setRoom({ ...room, capacity: parseInt(v) })}
          type="number" isRequired id="capacity" name="capacity" size={2} />
      </Td>
      <Td dataLabel={columns[2]}>
        <FormSelect value={room.hardwareLevel} onChange={(ev, v) => setRoom({ ...room, hardwareLevel: v as HARDWARE_LEVEL })}
          id="hardwareLevel" name="hardwareLevel">
          {
            Object.values(HARDWARE_LEVEL).map((level) => (
              <FormSelectOption key={level} value={level} label={level}>{level}</FormSelectOption>
            ))
          }
        </FormSelect>
      </Td>
      <Td dataLabel={columns[3]}>
        <Checkbox id="dualMonitors" name="dualMonitors" isChecked={room.dualMonitors}
          onChange={(ev, v) => setRoom({ ...room, dualMonitors: v })} />
      </Td>
      <Td dataLabel={columns[4]}>
        <TextArea value={room.comments || ''} onChange={(ev, v) => setRoom({ ...room, comments: v })}
          type="text" isRequired id="comments" name="comments" />
      </Td>
      <Td dataLabel={columns[4]}>
        <ActionList>
          <ActionListItem>
            <Button icon={<Icon><CheckIcon /></Icon>} onClick={onRoomSave} variant='plain' />
          </ActionListItem>
          <ActionListItem>
            <Button icon={<Icon><TimesIcon /></Icon>} onClick={onRoomCancel} variant='plain' />
          </ActionListItem>
        </ActionList>
      </Td>
    </Tr>
  );

  const roomRowDisplay = (room: FacilityRoom, index: number) => {
    return (
      <Tr key={index}>
        <Td dataLabel={columns[0]}>{room.name}</Td>
        <Td dataLabel={columns[1]}>{room.capacity}</Td>
        <Td dataLabel={columns[2]}>{room.hardwareLevel}</Td>
        <Td dataLabel={columns[3]}>{room.dualMonitors ? 'Yes' : 'No'}</Td>
        <Td dataLabel={columns[4]}>{room.comments}</Td>
        <Td dataLabel={columns[4]}>
          <ActionList>
            <ActionListItem>
              <Tooltip content="Edit">
                <Button icon={<Icon size='lg'><PencilAltIcon/></Icon>} 
                  variant="plain" id="edit-button" onClick={(ev) => onEditRoomClick(ev, room)} isDisabled={isEditRoomEnabled} />
              </Tooltip>
            </ActionListItem>
            <ActionListItem>
              <RemoveButton confirmationMessage='Confirm room removal?' onRemoveConfirm={() => onRemoveRoom(room.id)} isDisabled={isEditRoomEnabled} />
            </ActionListItem>
          </ActionList>
        </Td>
      </Tr>
    );
  };

  if (notFound) {
    return (
      <NotFoundState
        entityLabel="facility"
        identifier={id}
        backTo={{ path: '/facilities', label: 'Back to facilities' }}
      />
    );
  }

  const changeLogTitle = facility.name;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = facility.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="facility"
      id={facility.id}
      entityLabel="this facility"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={id === 'new' ? 'New Facility' : facility.name || 'Editing Facility'}
        actions={
          <ActionList>
            <ActionListItem>
              <Button variant="primary" id="save-button" isDisabled={!isDirty} onClick={onSaveClick}>Save</Button>
            </ActionListItem>
            <ActionListItem>
              <Button variant="link" id="cancel-button" isDisabled={!isDirty} onClick={onCancelClick}>Cancel</Button>
            </ActionListItem>
            <ActionListItem>
              <PageActionsMenu
                items={changeLogItems}
                onRefresh={onRefresh}
                onRemove={onRemove}
                isRemoveDisabled={id === 'new'}
                removeConfirmationMessage={`Confirm removal of "${facility.name}"?`}
              />
            </ActionListItem>
          </ActionList>
        }
      />
      <PageSection hasBodyWrapper isWidthLimited>
        <DescriptionList columnModifier={{ default: '2Col' }}>
          <DescriptionListGroup>
            <InlineEdit label='Name' value={facility.name} onSave={(v) => v !== facility.name && updateFacility({ ...facility, name: v as string })}
              type='TextInput' isGlobalEdition={isEditEnabled} id="name" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Contact Person' value={facility.contactPerson || ''} onSave={(v) => v !== facility.contactPerson && updateFacility({ ...facility, contactPerson: v as string })}
              type='TextInput' isGlobalEdition={isEditEnabled} id="contactPerson" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Address</DescriptionListTerm>
            <DescriptionListDescription>
              <FormAddress value={facility.address} id="address" name="address" onChange={(e, v) => v !== facility.address && updateFacility({ ...facility, address: v })} />
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Contact Email' value={facility.contactEmail || ''} onSave={(v) => v !== facility.contactEmail && updateFacility({ ...facility, contactEmail: v as string })}
              type='TextInput' isGlobalEdition={isEditEnabled} id="contactEmail"
              displayAs='Link' href={'mailto:' + facility.contactEmail} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Daily Rate' value={facility.dailyRate || ''} onSave={(v) => v !== facility.dailyRate && updateFacility({ ...facility, dailyRate: parseFloat(v as string) })}
              type='TextInput' isGlobalEdition={isEditEnabled} id="dailyRate" />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Contact Phone' value={facility.contactPhone || ''} type='Telephone'
              onSave={(v) => v !== facility.contactPhone && updateFacility({ ...facility, contactPhone: v as string })} isGlobalEdition={isEditEnabled} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Contact Mobile' value={facility.contactMobile || ''} type='Telephone'
              onSave={(v) => v !== facility.contactMobile && updateFacility({ ...facility, contactMobile: v as string })} isGlobalEdition={isEditEnabled} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Comments' value={facility.comments || ''} onSave={(v) => v !== facility.comments && updateFacility({ ...facility, comments: v as string })}
              type='TextArea' isGlobalEdition={isEditEnabled} id="comments" />
          </DescriptionListGroup>
        </DescriptionList>
      </PageSection>
      <PageSection hasBodyWrapper isWidthLimited>
        <PageSection hasBodyWrapper={false} isFilled>
          <SplitItem>
            <Title headingLevel="h1" size="lg">Rooms</Title>
          </SplitItem>
          <Card>
            {tableToolbar}
            <Table variant="compact" aria-label="Compact Table">
              <Thead>
                <Tr>
                  <Th key={0}>Name</Th>
                  <Th key={1}>Capacity</Th>
                  <Th key={2}>Hardware Level</Th>
                  <Th key={3}>Dual Monitors</Th>
                  <Th key={4}>Comments</Th>
                  <Th key={5}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row, index) => (
                    isEditRoomEnabled && room.id === row.id ? roomEditForm : roomRowDisplay(row, index)
                ))}
                {isAddingRoom && isEditRoomEnabled ? roomEditForm : ''}
              </Tbody>
            </Table>
          </Card>
        </PageSection>
      </PageSection>
    </EntityChangeLog>
  );
};

export { FacilityDetail };
